import { Flex, Slider, SliderTrack, SliderFilledTrack, SliderThumb, VStack, Box, Link, Text } from "@chakra-ui/react";
import { DropZone } from "components/controls";
import { FC, forwardRef, useRef, Ref } from 'react';
import AvatarEditor from "react-avatar-editor";
import { AiOutlineZoomIn } from '@react-icons/all-files/ai/AiOutlineZoomIn';
import { SimpleGrid, GridItem } from "@chakra-ui/layout";
import { useToastNotification } from "lib/hooks";

type Props = {
  acceptedFileTypes: string[];
  setThumbnailState: (updatedState: Record<string, any>) => void;
  thumbnailState: any;
  handleFiles: (files: FileList) => void;
  ref: Ref<any>;
}

export const UploadAvatarForm: FC<Props> = forwardRef(({
   acceptedFileTypes,
   setThumbnailState,
   thumbnailState,
   handleFiles
 }, ref: Ref<any>
) => {
  const fileRef = useRef(null);
  const { errorToast } = useToastNotification();

  const onFileAdd = (files) => {
    try {
      handleFiles(files);
    } catch (err) {
      errorToast({ title: 'Error', description: err });
    }
  }

  const openFileDialog = () => {
    fileRef.current.click()
  }

  return (
    <VStack width="100%" spacing={5}>
      <SimpleGrid
        templateColumns={"1fr 1fr"}
        templateRows={"auto auto"}
        gap={5}
        alignItems={"center"}
      >
        <GridItem>
          <Box
            width={"200px"}
            height={"200px"}
            layerStyle="with-dashed-border"
            borderColor="brand.500"
            borderRadius={"50%"}
            overflow="hidden"
          >
            <DropZone onDrop={(files) => onFileAdd(files)}>
              {!thumbnailState.pickImg && (
                <Flex height="200px" alignItems="center">
                  <Text textAlign="center" variant="secondary" width="100%">Drop image here</Text>
                </Flex>
              )}
              {thumbnailState.pickImg && (
                <AvatarEditor
                  image={thumbnailState.pickImg}
                  scale={thumbnailState.scale}
                  rotate={thumbnailState.rotate}
                  position={thumbnailState.position}
                  ref={ref}
                  onPositionChange={position => setThumbnailState({ ...thumbnailState, position })}
                  width={200}
                  height={200}
                  border={0}
                />
              )}
            </DropZone>
          </Box>
        </GridItem>
        <GridItem>
          <VStack spacing={3} alignItems={"start"}>
            <Text size={"sm"}>Drag &amp; drop an image or <Link color="brand.500" onClick={openFileDialog}>upload an image</Link> from your device.</Text>
            <Text size={"sm"}>Your file must be a png, gif or jpg. We recommend using square images.</Text>
          </VStack>
         <input
            type="file"
            accept={acceptedFileTypes.join(',')}
            id={`uploader-${thumbnailState.id}`}
            multiple ref={fileRef}
            onChange={() => onFileAdd(fileRef.current.files)}
            style={{ display: 'none' }}
          />
        </GridItem>
        <GridItem colSpan={2}>
          {thumbnailState.pickImg && (
            <Box width="96%" m="10px auto 0 auto">
              <Slider
                aria-label="zoom"
                min={1}
                max={5}
                step={0.025}
                value={thumbnailState.scale}
                onChange={(scale) => setThumbnailState({ ...thumbnailState, scale })}
              >
                <SliderTrack>
                  <SliderFilledTrack bgColor="brand.500" />
                </SliderTrack>
                <SliderThumb boxSize={7} backgroundColor="brand.500">
                  <Box as={AiOutlineZoomIn} color="#FFFFFF" />
                </SliderThumb>
              </Slider>
            </Box>
          )}
        </GridItem>
      </SimpleGrid>
    </VStack>
  )
})

UploadAvatarForm.displayName = "UploadAvatarForm";
